import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, HStack, Image, Text, VStack, useToast } from "@chakra-ui/react";
import { fetchAttachmentUrl } from "../../services/messagingService";
import { formatBytes, isImageType } from "../../utils/chatAttachments";
import PaperclipIcon from "./PaperclipIcon";

const FRESH_MS = 50 * 60 * 1000; // links are signed for 60 min

// One attachment's signed link.
// - fresh(force): a usable URL — reused for 50 minutes, refetched after that
//   (or whenever `force` is set).
// - open(): opens it in a new tab, toasting if the link can't be fetched.
// - retryOnce(): refetches it a single time (thumbnail missing a link, or one
//   that failed to load); a failure is logged, not swallowed.
function useAttachmentLink(channelId, att) {
  const [url, setUrl] = useState(att.url || null);
  const fetchedAt = useRef(att.url ? Date.now() : 0);
  const retried = useRef(false);
  const toast = useToast();

  const fresh = useCallback(
    async (force) => {
      if (!force && url && Date.now() - fetchedAt.current < FRESH_MS) return url;
      const res = await fetchAttachmentUrl(channelId, att.path);
      fetchedAt.current = Date.now();
      setUrl(res.url);
      return res.url;
    },
    [channelId, att.path, url],
  );

  const open = useCallback(async () => {
    try {
      window.open(await fresh(false), "_blank", "noopener,noreferrer");
    } catch (err) {
      toast({ title: "Couldn't open attachment", description: err.message, status: "error", duration: 4000 });
    }
  }, [fresh, toast]);

  const retryOnce = useCallback(() => {
    if (retried.current) return;
    retried.current = true;
    fresh(true).catch((err) => console.warn("Messaging: attachment link refresh failed", err));
  }, [fresh]);

  return [url, fresh, open, retryOnce];
}

function ImageAttachment({ channelId, att, onLoad }) {
  const [url, , open, retryOnce] = useAttachmentLink(channelId, att);
  // No link at all (the server couldn't sign one): an <img> without a src
  // never fires onError, so fetch one now instead of waiting for a click.
  // That uses up the image's one retry.
  useEffect(() => {
    if (!url) retryOnce();
  }, [url, retryOnce]);
  return (
    <Image
      src={url || undefined}
      alt={att.name}
      aria-label={`Open ${att.name}`}
      role="button"
      tabIndex={0}
      loading="lazy"
      maxW="220px"
      maxH="220px"
      objectFit="cover"
      borderRadius="md"
      cursor="pointer"
      _focusVisible={{ boxShadow: "outline" }}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      onError={retryOnce}
      onLoad={onLoad}
    />
  );
}

function FileAttachment({ channelId, att, isOwn }) {
  const [, , open] = useAttachmentLink(channelId, att);
  return (
    <HStack
      as="button"
      type="button"
      aria-label={`Open ${att.name}`}
      onClick={open}
      spacing={2}
      px={3}
      py={1.5}
      borderRadius="md"
      borderWidth="1px"
      bg={isOwn ? "blue.50" : "white"}
      _hover={{ bg: "gray.50" }}
      maxW="260px"
    >
      <PaperclipIcon flexShrink={0} />
      <Text as="span" fontSize="sm" noOfLines={1}>{att.name}</Text>
      <Text as="span" fontSize="xs" color="gray.500" flexShrink={0}>· {formatBytes(att.size)}</Text>
    </HStack>
  );
}

// Files on a chat message: images as thumbnails, everything else as chips.
// Links come signed for an hour; older ones are refreshed on click / error.
// onImageLoad fires when a thumbnail has loaded (and so taken its height).
export default function MessageAttachments({ channelId, attachments, isOwn, onImageLoad }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <VStack align={isOwn ? "flex-end" : "flex-start"} spacing={1} mt={1}>
      {attachments.map((att) =>
        isImageType(att.type) ? (
          <Box key={att.path}><ImageAttachment channelId={channelId} att={att} onLoad={onImageLoad} /></Box>
        ) : (
          <FileAttachment key={att.path} channelId={channelId} att={att} isOwn={isOwn} />
        ),
      )}
    </VStack>
  );
}
